import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Box, Divider, Typography, Tooltip, IconButton } from '@mui/material'
import { Label } from 'frame/components'
import SingleSelectInline from './single-select-inline'
import { hasRoles } from 'acl'
import { capitalize } from 'utils'

import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'

const SelectableValueText = ({
  id,
  color,
  format,
  label,
  large,
  noNone,
  options,
  outline,
  value,
  onCancel,
  onSave,
  onChange,
  userRoles,
  disabled,
  noLabel,
  toggleEdit, // to toggle edit mode from outside this valueText component
  onEdit // custom function passed to call on editmode select
}) => {

  const editable = !disabled && !hasRoles(['fleetcoordinator'], userRoles)
  const [editMode, setEditMode] = useState(false)

  useEffect(() => {
    if (toggleEdit) {
      setEditMode(false)
    }
  }, [toggleEdit])

  const restyle = color
    ? { color }
    : {}

  if (format === 'number' && value) {
    value = Number(value).toLocaleString('en-NZ')
  }
  else if (format === 'currency' && value) {
    value = Number(value).toLocaleString('en-NZ', {
      style: 'currency',
      currency: 'NZD',
    })
  }
  else if (format && value && ! editMode) {
    value = moment(value).format(format)
  }

  const onSaveHandler = () => {
    if (editMode) {
      onSave && onSave(value)
    }
    setEditMode(! editMode)
    onEdit && onEdit() // call custom onEdit() function if onEdit prop exists
  }

  const onClearHandler = () => {
    setEditMode(! editMode)
    onCancel && onCancel()
  }

  const displayValueField = () =>
    outline ? (
      <Label
        color={outline}
        variant="outlined">
        {value ? capitalize(value) : 'N/A'}
      </Label>
    ) : (
      <Typography
        style={restyle}
        variant="body1">
        {value === 'n/a' ? 'N/A' : value ? capitalize(value) : '-'}
      </Typography>
    )

  const displayEditField = () => (
    <Box
      sx={{
        flex: 1,
        position: 'relative',
      }}>
      <Box
        sx={{
          position: 'absolute',
          top: '-5px',
          left:'-8px',
          width: '100%',
        }}>
        <SingleSelectInline
          noNone={noNone}
          options={options}
          value={value}
          onChange={onChange} />
      </Box>
    </Box>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          position: 'relative',
          padding: 1,
        }}>
        {!noLabel && (
          <Typography
            sx={{
              paddingRight: 1,
              minWidth: large ? '150px' : '100px',
              fontWeight: '500',
            }}
            variant="body1">
            {label}
          </Typography>
        )}

        {editMode ? displayEditField() : displayValueField()}

        {editable && (
          <Box
            sx={{
              display: 'flex',
              width: '50px',
              height: '20px',
            }}/>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
          }}>
          {editable && (
            <Tooltip
              sx={{
                marginLeft: 1,
              }}
              title={editMode ? 'Save' : 'Edit'}>
              <IconButton
                size="small"
                onClick={onSaveHandler}>
                {editMode ? <SaveIcon
                  sx={{
                    width: 20,
                    height: 20,
                  }}/> : <EditIcon
                  sx={{
                    width: 20,
                    height: 20,
                  }}/>}
              </IconButton>
            </Tooltip>
          )}
          {editMode && (
            <Tooltip
              sx={{
                marginLeft: 1,
              }}
              title="Cancel">
              <IconButton
                size="small"
                onClick={onClearHandler}>
                <ClearIcon
                  sx={{
                    width: 20,
                    height: 20,
                  }}/>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      {!noLabel && <Divider />}
    </Box>
  )
}

SelectableValueText.propTypes = {
  id: PropTypes.string,
  color: PropTypes.string,
  format: PropTypes.string,
  label: PropTypes.string.isRequired,
  large: PropTypes.bool,
  noNone: PropTypes.bool,
  options: PropTypes.array.isRequired,
  outline: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]),
  onCancel: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabled: PropTypes.bool,
  noLabel: PropTypes.bool,
  toggleEdit: PropTypes.bool,
  onEdit: PropTypes.func,
}

const mapStateToProps = ({ settings }) => ({
  userRoles: settings.roles,
})

const mapDispatchToProps = {}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SelectableValueText)
